import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ListingFlow, Module, Page, PatternLibraryElement } from '../models/listingFlow';
import { ChipGroupUIElement, GroupUIElement } from '../models/uiElements';
import { ensureUUIDs } from '../utils/uuidUtils';
import { logger } from '../utils/logger';

interface EditorState {
  currentFlow: ListingFlow | null;
  undoStack: ListingFlow[];
  redoStack: ListingFlow[];
  selectedElement: PatternLibraryElement | null;
  selectedElementPath: number[]; // Pfad zum ausgewählten Element
  isDirty: boolean;
  selectedPageId: string | null; // ID der ausgewählten Seite
  dialogs: { [key: string]: boolean }; // Zustand für verschiedene Dialoge
  isSelectionMode: boolean; // Selektionsmodus für Multi-Select
  selectedElementPaths: number[][]; // Array von Pfaden für Multi-Selektion
}

type Action =
  | { type: 'SET_FLOW'; flow: ListingFlow }
  | { type: 'UPDATE_FLOW'; flow: ListingFlow }
  | { type: 'SELECT_ELEMENT'; element: PatternLibraryElement | null }
  | { type: 'SELECT_ELEMENT_BY_PATH'; path: number[]; pageId: string }
  | { type: 'ADD_ELEMENT'; element: PatternLibraryElement; pageId: string }
  | { type: 'REMOVE_ELEMENT'; elementIndex: number; pageId: string }
  | { type: 'MOVE_ELEMENT'; sourceIndex: number; targetIndex: number; pageId: string }
  | { type: 'ADD_SUB_ELEMENT'; element: PatternLibraryElement; path: number[]; pageId: string }
  | { type: 'REMOVE_SUB_ELEMENT'; path: number[]; pageId: string }
  | { type: 'MOVE_SUB_ELEMENT'; sourcePath: number[]; targetPath: number[]; pageId: string }
  | { type: 'ADD_PAGE'; page: Page }
  | { type: 'UPDATE_PAGE'; page: Page; viewPage?: Page }
  | { type: 'REMOVE_PAGE'; pageId: string }
  | { type: 'SELECT_PAGE'; pageId: string }
  | { type: 'MOVE_PAGE'; sourceIndex: number; targetIndex: number }
  | { type: 'TOGGLE_DIALOG'; payload: { dialog: string; open: boolean } } // Neue Action
  | { type: 'TOGGLE_SELECTION_MODE' } // Selektionsmodus umschalten
  | { type: 'TOGGLE_MULTI_SELECT'; path: number[]; pageId: string } // Element zur Multi-Selektion hinzufügen/entfernen
  | { type: 'CLEAR_MULTI_SELECT' } // Multi-Selektion leeren
  | { type: 'WRAP_IN_GROUP'; payload: { paths: number[][]; groupTitle: string; groupFieldId: string; pageId: string } } // Elemente in Gruppe zusammenfassen
  | { type: 'UNGROUP'; payload: { path: number[]; pageId: string } } // Gruppierung auflösen
  | { type: 'IMPORT_PAGES'; payload: { editPages: Page[]; viewPages: Page[] } } // Seiten aus externer Datei importieren
  | { type: 'COPY_ELEMENT_TO_PAGE'; payload: { targetPageId: string; clonedElement: PatternLibraryElement; position: 'top' | 'bottom' } } // Element auf andere Seite kopieren
  | { type: 'ADD_MODULE'; module: Module } // Modul zum Katalog hinzufügen
  | { type: 'UPDATE_MODULE'; moduleId: string; updates: Partial<Module> } // Modul bearbeiten (bei id-Änderung werden Referenzen mit-migriert)
  | { type: 'REMOVE_MODULE'; moduleId: string } // Modul entfernen und referenzierende module_id bereinigen
  | { type: 'UNDO' }
  | { type: 'REDO' };

const initialState: EditorState = {
  currentFlow: null,
  undoStack: [],
  redoStack: [],
  selectedElement: null,
  selectedElementPath: [],
  isDirty: false,
  selectedPageId: null,
  dialogs: {}, // Initial leer
  isSelectionMode: false,
  selectedElementPaths: [],
};

export interface EditorContextType {
  state: EditorState;
  dispatch: React.Dispatch<Action>;
}

export const EditorContext = createContext<EditorContextType | null>(null);

// Hilfsfunktion, um ein Element anhand eines Pfades zu finden
/**
 * Konvertiert ein Element in ein PatternLibraryElement wenn nötig
 */
const ensurePatternLibraryElement = (element: any): PatternLibraryElement => {
  if (element && element.element) {
    return element;
  }
  return { element };
};

/**
 * Bestimmt den Containertyp eines Elements
 * @param element Das zu prüfende Element
 * @returns Der Containertyp des Elements ('group', 'array', 'chipgroup', 'custom', 'subflow', 'none')
 */
export const getContainerType = (element: PatternLibraryElement | null): string => {
  if (!element || !element.element) return 'none';

  // Für Subflow-Objekte (die keinen pattern_type haben, aber einen 'type')
  if (!element.element.pattern_type && (element.element as any).type) {
    return 'subflow';
  }

  // Für reguläre UIElemente mit pattern_type
  switch (element.element.pattern_type) {
    case 'GroupUIElement':
      return 'group';
    case 'ArrayUIElement':
      return 'array';
    case 'ChipGroupUIElement':
      return 'chipgroup';
    case 'CustomUIElement':
      return 'custom';
    default:
      return 'none';
  }
};

/**
 * Bestimmt den Pfadkontext für einen gegebenen Pfad
 * @param elements Die Elemente, in denen der Pfad navigiert wird
 * @param path Der zu analysierende Pfad
 * @returns Ein Objekt mit Informationen über den Pfadkontext
 */
export interface PathContext {
  containerType: string;  // Der Typ des Containers am Ende des Pfads
  containerPath: number[]; // Der Pfad zum Container
  childrenProperty: string; // Die Eigenschaft, die die Kinder enthält ('elements', 'chips', 'sub_flows')
  isValid: boolean; // Ob der Pfad gültig ist
}

export const getPathContext = (elements: PatternLibraryElement[], path: number[]): PathContext => {
  logger.log('[getPathContext] path:', path);

  // Standardwerte für den Pfadkontext
  const defaultContext: PathContext = {
    containerType: 'none',
    containerPath: [],
    childrenProperty: 'elements',
    isValid: false
  };

  if (path.length === 0) {
    // Wir sind auf der obersten Ebene
    return {
      containerType: 'root',
      containerPath: [],
      childrenProperty: 'elements',
      isValid: true
    };
  }

  // Wenn der Pfad nur ein Element enthält, ist der Container die oberste Ebene
  if (path.length === 1) {
    const element = elements[path[0]];
    if (!element) return defaultContext;

    return {
      containerType: 'root',
      containerPath: [],
      childrenProperty: 'elements',
      isValid: true
    };
  }

  // Für längere Pfade müssen wir den Pfad bis zum vorletzten Element navigieren
  const parentPath = path.slice(0, -1);
  const parentElement = getElementByPath(elements, parentPath);

  if (!parentElement) return defaultContext;

  const containerType = getContainerType(parentElement);

  // Bestimme die Eigenschaft, die die Kinder enthält
  let childrenProperty = 'elements';
  if (containerType === 'chipgroup') {
    childrenProperty = 'chips';
  } else if (containerType === 'custom' && (parentElement.element as any).sub_flows) {
    childrenProperty = 'sub_flows';
  } else if (containerType === 'subflow') {
    childrenProperty = 'elements';
  }

  return {
    containerType,
    containerPath: parentPath,
    childrenProperty,
    isValid: true
  };
};

/**
 * Holt Unterelemente eines Elements basierend auf seinem Typ
 * Verbesserte Version, die alle Containertypen in doorbit_original.json unterstützt
 */
export const getSubElements = (element: PatternLibraryElement): PatternLibraryElement[] => {
  if (!element || !element.element) return [];

  logger.log('[getSubElements] Element:', element.element.pattern_type || (element.element as any).type || 'unknown');

  // Für Subflow-Objekte (die als parentElement übergeben werden, wenn currentPath auf sie zeigt)
  // Ein Subflow-Objekt selbst hat keinen pattern_type, aber einen 'type' (z.B. 'POI')
  // und enthält 'elements'.
  if (!element.element.pattern_type && (element.element as any).type) {
    logger.log('[getSubElements] Subflow erkannt, type:', (element.element as any).type);
    // Die Kinder eines Subflow-Objekts sind dessen 'elements'-Array.
    const elementsInSubFlow = (element.element as any).elements || [];
    // Anmerkung: sub_elements wird hier nicht berücksichtigt, um konsistent mit getChildElements zu sein
    return elementsInSubFlow.map(ensurePatternLibraryElement);
  }

  // Für reguläre UIElemente mit pattern_type
  switch (element.element.pattern_type) {
    case 'GroupUIElement':
    case 'ArrayUIElement':
      logger.log('[getSubElements] Group/Array erkannt, elements:', ((element.element as any).elements || []).length);
      return ((element.element as any).elements || []).map(ensurePatternLibraryElement);
    case 'ChipGroupUIElement':
      // Chips sind BooleanUIElements, müssen aber als PatternLibraryElement behandelt werden
      logger.log('[getSubElements] ChipGroup erkannt, chips:', ((element.element as any).chips || []).length);
      return ((element.element as any).chips || []).map((chip: any) => ensurePatternLibraryElement(chip));
    case 'CustomUIElement':
      // Wenn ein CustomUIElement das parentElement ist, sind seine "Kinder" im Kontext des Drilldowns
      // zunächst seine sub_flows. Das Navigieren IN einen Subflow (um dessen Elemente anzuzeigen)
      // wird dadurch gehandhabt, dass getElementByPath das Subflow-Objekt als neues parentElement liefert.
      logger.log('[getSubElements] CustomUIElement erkannt, prüfe auf sub_flows und elements');
      if ((element.element as any).sub_flows) {
        return ((element.element as any).sub_flows || []).map(ensurePatternLibraryElement);
      }
      // Hat ein CustomUIElement keine sub_flows, könnte es direkte 'elements' haben.
      if (Array.isArray((element.element as any).elements)) {
        return ((element.element as any).elements || []).map(ensurePatternLibraryElement);
      }
      return []; // CustomUIElement ohne sub_flows und ohne elements hat keine Kinder in diesem Kontext
  }

  // Generische Fallbacks für andere Typen, die möglicherweise Kinder haben könnten
  // (z.B. SingleSelectionUIElement mit 'options', KeyValueListUIElement mit 'items')
  const potentialChildArrays = ['elements', 'items', 'options'];
  for (const key of potentialChildArrays) {
    const childArray = (element.element as any)[key];
    if (Array.isArray(childArray) && childArray.length > 0) {
      // Prüfen, ob die Elemente der Arrays bereits PatternLibraryElement sind oder rohe Objekte
      return childArray.map((item: any) => ensurePatternLibraryElement(item));
    }
  }

  // Fallback: Wenn kein spezifischer Kind-Array-Typ gefunden wurde, aber eine beliebige Array-Eigenschaft existiert,
  // die Objekte enthält (weniger wahrscheinlich für Drilldown, aber zur Sicherheit)
  for (const key in element.element) {
    const value = (element.element as any)[key];
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      // Hier ist Vorsicht geboten, da nicht jedes Array von Objekten navigierbare Kinder darstellt.
      // Für den Drilldown sind primär 'elements' in Group/Array/SubFlow und 'sub_flows' in CustomUIElement relevant.
      return value.map((item: any) => ensurePatternLibraryElement(item));
    }
  }

  return [];
};

/**
 * Findet ein Element anhand eines Pfades in der Element-Hierarchie
 * Verbesserte Version, die alle Containertypen in doorbit_original.json unterstützt
 */
export const getElementByPath = (elements: PatternLibraryElement[], path: number[]): PatternLibraryElement | null => {
  if (path.length === 0 || !elements || elements.length === 0) return null;

  const [index, ...restPath] = path;
  if (index >= elements.length) return null;

  const element = elements[index];
  if (restPath.length === 0) return element;

  logger.log('[getElementByPath] Navigiere zu Element:',
    element.element.pattern_type || (element.element as any).type || 'unknown',
    'restPath:', restPath);

  // Rekursiv durch die Unterelemente navigieren
  const subElements = getSubElements(element);
  if (subElements.length > 0) {
    // Spezielle Behandlung für CustomUIElement mit sub_flows
    if (element.element.pattern_type === 'CustomUIElement' && (element.element as any).sub_flows) {
      const subFlowIndex = restPath[0];
      const subFlows = (element.element as any).sub_flows || [];

      logger.log('[getElementByPath] CustomUIElement mit sub_flows erkannt, subFlowIndex:', subFlowIndex, 'subFlows.length:', subFlows.length);

      if (subFlowIndex < subFlows.length) {
        if (restPath.length === 1) {
          // Wir wollen das SubFlow-Objekt selbst zurückgeben
          logger.log('[getElementByPath] Gebe SubFlow-Objekt zurück');
          return ensurePatternLibraryElement(subFlows[subFlowIndex]);
        } else {
          // Wir wollen ein Element innerhalb des SubFlows zurückgeben
          const subFlowElement = ensurePatternLibraryElement(subFlows[subFlowIndex]);
          logger.log('[getElementByPath] Navigiere in SubFlow:', (subFlowElement.element as any).type || 'unknown');

          // Hier verwenden wir getSubElements für das SubFlow-Element, um seine Unterelemente zu finden
          const subFlowChildElements = getSubElements(subFlowElement);

          // Dann navigieren wir weiter mit dem Rest des Pfades
          return getElementByPath(subFlowChildElements, restPath.slice(1));
        }
      }
    }

    // Spezielle Behandlung für ChipGroupUIElement mit chips
    else if (element.element.pattern_type === 'ChipGroupUIElement' && (element.element as any).chips) {
      logger.log('[getElementByPath] ChipGroupUIElement mit chips erkannt');
      // Hier müssen wir sicherstellen, dass die chips korrekt als PatternLibraryElements behandelt werden
      return getElementByPath(subElements, restPath);
    }

    // Standardbehandlung für andere Containertypen
    else {
      logger.log('[getElementByPath] Standard-Container erkannt, navigiere zu Unterelementen');
      return getElementByPath(subElements, restPath);
    }
  }

  logger.log('[getElementByPath] Keine Unterelemente gefunden oder ungültiger Pfad');
  return null;
};

// Hilfsfunktion zum Aktualisieren tief verschachtelter Elemente
/*
const updateElementAtPath = (
  elements: PatternLibraryElement[],
  path: number[],
  updater: (element: PatternLibraryElement) => PatternLibraryElement
): PatternLibraryElement[] => {
  if (path.length === 0) return elements;

  const [index, ...restPath] = path;
  if (index >= elements.length) return elements;

  return elements.map((element, i) => {
    if (i !== index) return element;

    if (restPath.length === 0) {
      // Letzter Pfadteil erreicht, Element aktualisieren
      return updater(element);
    }

    // In die Tiefe gehen
    if (element.element.pattern_type === 'GroupUIElement' ||
        element.element.pattern_type === 'ArrayUIElement') {
      const subElements = [...((element.element as any).elements || [])];
      const updatedSubElements = updateElementAtPath(subElements, restPath, updater);

      return {
        ...element,
        element: {
          ...(element.element as any),
          elements: updatedSubElements
        }
      };
    }

    // Für ChipGroupUIElement mit chips-Array
    if (element.element.pattern_type === 'ChipGroupUIElement') {
      logger.log('updateElementAtPath für ChipGroup:', element);
      const chipElements = ((element.element as any).chips || []).map((chip: any) => ({
        element: chip
      }));
      const updatedChipElements = updateElementAtPath(chipElements, restPath, updater);

      // Extrahiere die BooleanUIElements aus den PatternLibraryElements zurück
      const updatedChips = updatedChipElements.map((chipElement: any) => chipElement.element);
      logger.log('Aktualisierte Chips:', updatedChips);

      return {
        ...element,
        element: {
          ...(element.element as any),
          chips: updatedChips
        }
      };
    }

    return element;
  });
};
*/

/**
 * Hilfsfunktion zum Hinzufügen eines Elements an einem bestimmten Pfad
 * Verbesserte Version, die alle Containertypen in doorbit_original.json unterstützt
 */
const addElementAtPath = (
  elements: PatternLibraryElement[],
  path: number[],
  newElement: PatternLibraryElement
): PatternLibraryElement[] => {
  logger.log('[addElementAtPath] path:', path, 'newElement.pattern_type:', newElement.element.pattern_type);

  if (path.length === 0) {
    // Hinzufügen am Ende der Liste
    logger.log('[addElementAtPath] Füge Element am Ende der Liste hinzu');
    return [...elements, newElement];
  }

  const [index, ...restPath] = path;
  if (index > elements.length) {
    logger.log('[addElementAtPath] Index außerhalb des gültigen Bereichs');
    return elements;
  }

  if (restPath.length === 0) {
    // Element an dieser Stelle einfügen
    logger.log('[addElementAtPath] Füge Element an Position', index, 'ein');
    const result = [...elements];
    result.splice(index, 0, newElement);
    return result;
  }

  return elements.map((element, i) => {
    if (i !== index) return element;

    logger.log('[addElementAtPath] Navigiere zu Element:',
      element.element.pattern_type || (element.element as any).type || 'unknown');

    // Spezielle Behandlung für CustomUIElement mit sub_flows
    if (element.element.pattern_type === 'CustomUIElement' && (element.element as any).sub_flows) {
      const subFlowIndex = restPath[0];
      const subFlows = [...((element.element as any).sub_flows || [])];

      logger.log('[addElementAtPath] CustomUIElement mit sub_flows erkannt, subFlowIndex:', subFlowIndex);

      // Wenn wir direkt in einen sub_flow einfügen wollen
      if (restPath.length === 1) {
        logger.log('[addElementAtPath] Füge neuen sub_flow hinzu');
        // Hier fügen wir einen neuen sub_flow hinzu
        subFlows.splice(subFlowIndex, 0, newElement.element);

        return {
          ...element,
          element: {
            ...(element.element as any),
            sub_flows: subFlows
          }
        };
      }

      // Wenn wir in einen bestehenden sub_flow einfügen wollen
      if (subFlowIndex < subFlows.length) {
        logger.log('[addElementAtPath] Füge Element in bestehenden sub_flow ein');
        const subFlow = subFlows[subFlowIndex];

        // Konvertiere den sub_flow zu einem PatternLibraryElement
        const subFlowElement = ensurePatternLibraryElement(subFlow);

        // Hole die Elemente des sub_flows
        const subFlowElements = ((subFlowElement.element as any).elements || []).map(ensurePatternLibraryElement);

        // Füge das neue Element in die Elemente des sub_flows ein
        const updatedSubFlowElements = addElementAtPath(subFlowElements, restPath.slice(1), newElement);

        // Aktualisiere den sub_flow
        subFlows[subFlowIndex] = {
          ...subFlow,
          elements: updatedSubFlowElements.map(el => el.element ? el : { element: el })
        };

        return {
          ...element,
          element: {
            ...(element.element as any),
            sub_flows: subFlows
          }
        };
      }
    }

    // In die Tiefe gehen für GroupUIElement oder ArrayUIElement
    else if (element.element.pattern_type === 'GroupUIElement' ||
        element.element.pattern_type === 'ArrayUIElement') {
      logger.log('[addElementAtPath] Group/Array erkannt, navigiere zu Unterelementen');
      const subElements = [...((element.element as any).elements || [])];
      const updatedSubElements = addElementAtPath(subElements, restPath, newElement);

      return {
        ...element,
        element: {
          ...(element.element as any),
          elements: updatedSubElements
        }
      };
    }

    // Für ChipGroupUIElement mit chips-Array
    else if (element.element.pattern_type === 'ChipGroupUIElement') {
      logger.log('[addElementAtPath] ChipGroup erkannt, verarbeite chips');
      // Wenn wir ein Element zu einer ChipGroup hinzufügen, muss es ein BooleanUIElement sein
      if (newElement.element.pattern_type !== 'BooleanUIElement') {
        logger.error('Nur BooleanUIElements können zu ChipGroups hinzugefügt werden');
        return element;
      }

      // Konvertiere die chips zu PatternLibraryElements für die Verarbeitung
      const chipElements = ((element.element as ChipGroupUIElement).chips || []).map(chip => ({
        element: chip
      }));

      // Füge das neue Element zum chips-Array hinzu
      const updatedChipElements = addElementAtPath(chipElements, restPath, newElement);

      // Extrahiere die BooleanUIElements aus den PatternLibraryElements zurück
      const updatedChips = updatedChipElements.map(chipElement => chipElement.element);

      return {
        ...element,
        element: {
          ...(element.element as ChipGroupUIElement),
          chips: updatedChips
        }
      };
    }

    logger.log('[addElementAtPath] Kein unterstützter Containertyp gefunden');
    return element;
  });
};

/**
 * Hilfsfunktion zum Entfernen eines Elements an einem bestimmten Pfad
 * Verbesserte Version, die alle Containertypen in doorbit_original.json unterstützt
 */
const removeElementAtPath = (
  elements: PatternLibraryElement[],
  path: number[]
): PatternLibraryElement[] => {
  logger.log('[removeElementAtPath] path:', path);

  if (path.length === 0) return elements;

  const [index, ...restPath] = path;
  if (index >= elements.length) {
    logger.log('[removeElementAtPath] Index außerhalb des gültigen Bereichs');
    return elements;
  }

  if (restPath.length === 0) {
    // Element an dieser Stelle entfernen
    logger.log('[removeElementAtPath] Entferne Element an Position', index);
    return elements.filter((_, i) => i !== index);
  }

  return elements.map((element, i) => {
    if (i !== index) return element;

    logger.log('[removeElementAtPath] Navigiere zu Element:',
      element.element.pattern_type || (element.element as any).type || 'unknown');

    // Spezielle Behandlung für CustomUIElement mit sub_flows
    if (element.element.pattern_type === 'CustomUIElement' && (element.element as any).sub_flows) {
      const subFlowIndex = restPath[0];
      const subFlows = [...((element.element as any).sub_flows || [])];

      logger.log('[removeElementAtPath] CustomUIElement mit sub_flows erkannt, subFlowIndex:', subFlowIndex);

      // Wenn wir einen sub_flow entfernen wollen
      if (restPath.length === 1) {
        logger.log('[removeElementAtPath] Entferne sub_flow');
        // Entferne den sub_flow
        const updatedSubFlows = subFlows.filter((_, i) => i !== subFlowIndex);

        return {
          ...element,
          element: {
            ...(element.element as any),
            sub_flows: updatedSubFlows
          }
        };
      }

      // Wenn wir ein Element innerhalb eines sub_flows entfernen wollen
      if (subFlowIndex < subFlows.length) {
        logger.log('[removeElementAtPath] Entferne Element aus sub_flow');
        const subFlow = subFlows[subFlowIndex];

        // Konvertiere den sub_flow zu einem PatternLibraryElement
        const subFlowElement = ensurePatternLibraryElement(subFlow);

        // Hole die Elemente des sub_flows
        const subFlowElements = ((subFlowElement.element as any).elements || []).map(ensurePatternLibraryElement);

        // Entferne das Element aus den Elementen des sub_flows
        const updatedSubFlowElements = removeElementAtPath(subFlowElements, restPath.slice(1));

        // Aktualisiere den sub_flow
        subFlows[subFlowIndex] = {
          ...subFlow,
          elements: updatedSubFlowElements.map(el => el.element ? el : { element: el })
        };

        return {
          ...element,
          element: {
            ...(element.element as any),
            sub_flows: subFlows
          }
        };
      }
    }

    // In die Tiefe gehen für GroupUIElement oder ArrayUIElement
    else if (element.element.pattern_type === 'GroupUIElement' ||
        element.element.pattern_type === 'ArrayUIElement') {
      logger.log('[removeElementAtPath] Group/Array erkannt, navigiere zu Unterelementen');
      const subElements = [...((element.element as any).elements || [])];
      const updatedSubElements = removeElementAtPath(subElements, restPath);

      return {
        ...element,
        element: {
          ...(element.element as any),
          elements: updatedSubElements
        }
      };
    }

    // Für ChipGroupUIElement mit chips-Array
    else if (element.element.pattern_type === 'ChipGroupUIElement') {
      logger.log('[removeElementAtPath] ChipGroup erkannt, verarbeite chips');
      // Konvertiere die chips zu PatternLibraryElements für die Verarbeitung
      const chipElements = ((element.element as ChipGroupUIElement).chips || []).map(chip => ({
        element: chip
      }));

      // Entferne das Element aus dem chips-Array
      const updatedChipElements = removeElementAtPath(chipElements, restPath);

      // Extrahiere die BooleanUIElements aus den PatternLibraryElements zurück
      const updatedChips = updatedChipElements.map(chipElement => chipElement.element);

      return {
        ...element,
        element: {
          ...(element.element as ChipGroupUIElement),
          chips: updatedChips
        }
      };
    }

    logger.log('[removeElementAtPath] Kein unterstützter Containertyp gefunden');
    return element;
  });
};

/**
 * Wendet eine Transformation auf das `module_id` eines Elements und rekursiv aller
 * verschachtelten Elemente an (Group/Array/Custom.elements, Custom/Page.sub_flows[].elements,
 * ChipGroup.chips). Gibt `undefined` zurück = `module_id` wird entfernt.
 */
const transformElementModuleId = (
  ple: PatternLibraryElement,
  fn: (moduleId: string | undefined) => string | undefined
): PatternLibraryElement => {
  const el: any = { ...ple.element };

  const newModuleId = fn(el.module_id);
  if (newModuleId === undefined) {
    delete el.module_id;
  } else {
    el.module_id = newModuleId;
  }

  if (Array.isArray(el.elements)) {
    el.elements = el.elements.map((child: PatternLibraryElement) => transformElementModuleId(child, fn));
  }

  if (Array.isArray(el.sub_flows)) {
    el.sub_flows = el.sub_flows.map((sf: any) => ({
      ...sf,
      elements: Array.isArray(sf.elements)
        ? sf.elements.map((child: PatternLibraryElement) => transformElementModuleId(child, fn))
        : sf.elements,
    }));
  }

  if (Array.isArray(el.chips)) {
    el.chips = el.chips.map((chip: any) => {
      const c = { ...chip };
      const nm = fn(c.module_id);
      if (nm === undefined) {
        delete c.module_id;
      } else {
        c.module_id = nm;
      }
      return c;
    });
  }

  return { element: el };
};

/**
 * Wendet eine module_id-Transformation auf eine Seitenliste an: auf die Seite selbst
 * und rekursiv auf alle (verschachtelten) Elemente und sub_flows. Immutable.
 */
const transformPagesModuleId = (
  pages: Page[],
  fn: (moduleId: string | undefined) => string | undefined
): Page[] =>
  pages.map(page => {
    const p: any = { ...page };

    const newModuleId = fn(p.module_id);
    if (newModuleId === undefined) {
      delete p.module_id;
    } else {
      p.module_id = newModuleId;
    }

    if (Array.isArray(p.elements)) {
      p.elements = p.elements.map((child: PatternLibraryElement) => transformElementModuleId(child, fn));
    }

    if (Array.isArray(p.sub_flows)) {
      p.sub_flows = p.sub_flows.map((sf: any) => ({
        ...sf,
        elements: Array.isArray(sf.elements)
          ? sf.elements.map((child: PatternLibraryElement) => transformElementModuleId(child, fn))
          : sf.elements,
      }));
    }

    return p as Page;
  });

function editorReducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'SET_FLOW':
      // Sicherstellen, dass alle Elemente UUIDs haben
      const flowWithUUIDs = ensureUUIDs(action.flow);

      return {
        ...state,
        currentFlow: flowWithUUIDs,
        undoStack: [],
        redoStack: [],
        selectedElement: null,
        selectedElementPath: [],
        isDirty: false,
        // Erste Seite als Standard auswählen, wenn verfügbar
        selectedPageId: flowWithUUIDs?.pages_edit.length > 0 ? flowWithUUIDs.pages_edit[0].id : null,
        isSelectionMode: false,
        selectedElementPaths: [],
      };

    case 'UPDATE_FLOW':
      return {
        ...state,
        undoStack: state.currentFlow ? [...state.undoStack, state.currentFlow] : state.undoStack,
        currentFlow: action.flow,
        redoStack: [],
        isDirty: true,
      };

    case 'ADD_MODULE': {
      if (!state.currentFlow) return state;
      const newFlow: ListingFlow = {
        ...state.currentFlow,
        modules: [...(state.currentFlow.modules ?? []), action.module],
      };
      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        isDirty: true,
      };
    }

    case 'UPDATE_MODULE': {
      if (!state.currentFlow) return state;
      const { moduleId, updates } = action;
      const updatedModules = (state.currentFlow.modules ?? []).map(m =>
        m.id === moduleId ? { ...m, ...updates } : m
      );
      let newFlow: ListingFlow = { ...state.currentFlow, modules: updatedModules };

      // Bei id-Änderung referenzierende module_id über alle Seiten/Elemente mit-migrieren
      if (updates.id && updates.id !== moduleId) {
        const rename = (mid: string | undefined) => (mid === moduleId ? updates.id! : mid);
        newFlow = {
          ...newFlow,
          pages_edit: transformPagesModuleId(newFlow.pages_edit, rename),
          pages_view: transformPagesModuleId(newFlow.pages_view, rename),
        };
      }

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        isDirty: true,
      };
    }

    case 'REMOVE_MODULE': {
      if (!state.currentFlow) return state;
      const { moduleId } = action;
      const clear = (mid: string | undefined) => (mid === moduleId ? undefined : mid);
      const newFlow: ListingFlow = {
        ...state.currentFlow,
        modules: (state.currentFlow.modules ?? []).filter(m => m.id !== moduleId),
        pages_edit: transformPagesModuleId(state.currentFlow.pages_edit, clear),
        pages_view: transformPagesModuleId(state.currentFlow.pages_view, clear),
      };
      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        isDirty: true,
      };
    }

    case 'SELECT_ELEMENT':
      return {
        ...state,
        selectedElement: action.element,
      };

    case 'SELECT_ELEMENT_BY_PATH': {
      if (!state.currentFlow) return state;

      const page = state.currentFlow.pages_edit.find(p => p.id === action.pageId);
      if (!page) return state;

      const selectedElement = getElementByPath(page.elements, action.path);

      return {
        ...state,
        selectedElement,
        selectedElementPath: action.path,
      };
    }

    case 'ADD_ELEMENT':
      if (!state.currentFlow) return state;

      const newFlowWithAdd = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) =>
          page.id === action.pageId
            ? { ...page, elements: [...page.elements, action.element] }
            : page
        ),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithAdd,
        redoStack: [],
        isDirty: true,
      };

    case 'REMOVE_ELEMENT':
      if (!state.currentFlow) return state;

      const newFlowWithRemove = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) =>
          page.id === action.pageId
            ? {
                ...page,
                elements: page.elements.filter((_, index) => index !== action.elementIndex),
              }
            : page
        ),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithRemove,
        redoStack: [],
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
      };

    case 'MOVE_ELEMENT':
      if (!state.currentFlow) return state;

      const newFlowWithMove = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) => {
          if (page.id !== action.pageId) return page;

          const elements = [...page.elements];
          const [moved] = elements.splice(action.sourceIndex, 1);
          elements.splice(action.targetIndex, 0, moved);

          return { ...page, elements };
        }),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithMove,
        redoStack: [],
        isDirty: true,
      };

    case 'ADD_SUB_ELEMENT': {
      if (!state.currentFlow) return state;

      const newFlowWithAddSub = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) => {
          if (page.id !== action.pageId) return page;

          if (action.path.length === 0) {
            // Top-Level Element hinzufügen
            return {
              ...page,
              elements: [...page.elements, action.element]
            };
          }

          // Element an einem bestimmten Pfad hinzufügen
          const updatedElements = addElementAtPath(page.elements, action.path, action.element);

          return {
            ...page,
            elements: updatedElements
          };
        }),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithAddSub,
        redoStack: [],
        isDirty: true,
      };
    }

    case 'REMOVE_SUB_ELEMENT': {
      if (!state.currentFlow) return state;

      const newFlowWithRemoveSub = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) => {
          if (page.id !== action.pageId) return page;

          // Element an einem bestimmten Pfad entfernen
          const updatedElements = removeElementAtPath(page.elements, action.path);

          return {
            ...page,
            elements: updatedElements
          };
        }),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithRemoveSub,
        redoStack: [],
        // Wenn das aktuell ausgewählte Element entfernt wurde, Auswahl zurücksetzen
        selectedElement: JSON.stringify(state.selectedElementPath).startsWith(JSON.stringify(action.path))
          ? null
          : state.selectedElement,
        selectedElementPath: JSON.stringify(state.selectedElementPath).startsWith(JSON.stringify(action.path))
          ? []
          : state.selectedElementPath,
        isDirty: true,
      };
    }

    case 'MOVE_SUB_ELEMENT': {
      if (!state.currentFlow) return state;

      const sourcePage = state.currentFlow.pages_edit.find(p => p.id === action.pageId);
      if (!sourcePage) return state;

      const sourceElement = getElementByPath(sourcePage.elements, action.sourcePath);
      if (!sourceElement) return state;

      // Erst entfernen, dann hinzufügen
      let newFlowWithRemove = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((page: Page) => {
          if (page.id !== action.pageId) return page;

          // Element an Quellpfad entfernen
          const updatedElements = removeElementAtPath(page.elements, action.sourcePath);

          return {
            ...page,
            elements: updatedElements
          };
        }),
      };

      const newFlowWithMove = {
        ...newFlowWithRemove,
        pages_edit: newFlowWithRemove.pages_edit.map((page: Page) => {
          if (page.id !== action.pageId) return page;

          // Element am Zielpfad hinzufügen
          const updatedElements = addElementAtPath(page.elements, action.targetPath, sourceElement);

          return {
            ...page,
            elements: updatedElements
          };
        }),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithMove,
        redoStack: [],
        isDirty: true,
      };
    }

    case 'UNDO':
      if (state.undoStack.length === 0) return state;

      const previousFlow = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        currentFlow: previousFlow,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: state.currentFlow ? [...state.redoStack, state.currentFlow] : state.redoStack,
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
        isSelectionMode: false,
        selectedElementPaths: [],
      };

    case 'REDO':
      if (state.redoStack.length === 0) return state;

      const nextFlow = state.redoStack[state.redoStack.length - 1];
      return {
        ...state,
        currentFlow: nextFlow,
        undoStack: state.currentFlow ? [...state.undoStack, state.currentFlow] : state.undoStack,
        redoStack: state.redoStack.slice(0, -1),
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
        isSelectionMode: false,
        selectedElementPaths: [],
      };

    case 'ADD_PAGE': {
      if (!state.currentFlow) return state;

      // Erstelle die entsprechende View-Seite für eine Edit-Seite
      const viewPage: Page = {
        ...action.page,
        pattern_type: 'CustomUIElement', // Verwende CustomUIElement für View-Seiten
        id: action.page.id.replace('edit-', 'view-'), // Ersetze "edit-" durch "view-"
        layout: '2_COL_RIGHT_WIDER', // Standardlayout für View-Seiten
        elements: [], // View-Seite beginnt mit leerer Elementliste
        related_pages: [{ // Verknüpfe mit der Edit-Seite
          viewing_context: 'EDIT',
          page_id: action.page.id
        }]
      };

      // Aktualisiere auch die Edit-Seite, um sie mit der View-Seite zu verknüpfen
      const editPage: Page = {
        ...action.page,
        related_pages: [{ // Verknüpfe mit der View-Seite
          viewing_context: 'VIEW',
          page_id: viewPage.id
        }]
      };

      const newFlow = {
        ...state.currentFlow,
        pages_edit: [...state.currentFlow.pages_edit, editPage],
        pages_view: [...state.currentFlow.pages_view, viewPage]
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        selectedPageId: action.page.id, // Neue Seite auswählen
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
      };
    }

    case 'REMOVE_PAGE': {
      if (!state.currentFlow) return state;

      // Prüfen, ob es die letzte Seite ist - wenn ja, nicht löschen
      if (state.currentFlow.pages_edit.length <= 1) {
        return state;
      }

      // Die View-Page hat eine andere ID als die Edit-Page (view- statt edit- Präfix)
      const viewPageIdToRemove = action.pageId.startsWith('edit-')
        ? action.pageId.replace('edit-', 'view-')
        : action.pageId;

      const newFlow = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.filter(page => page.id !== action.pageId),
        pages_view: state.currentFlow.pages_view.filter(page => page.id !== viewPageIdToRemove)
      };

      // Bestimme die nächste ausgewählte Seite
      let nextSelectedPageId = state.selectedPageId;
      if (state.selectedPageId === action.pageId) {
        // Wähle die erste verbleibende Seite aus, wenn die aktuelle Seite gelöscht wird
        nextSelectedPageId = newFlow.pages_edit.length > 0 ? newFlow.pages_edit[0].id : null;
      }

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        selectedPageId: nextSelectedPageId,
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
      };
    }

    case 'SELECT_PAGE': {
      return {
        ...state,
        selectedPageId: action.pageId,
        selectedElement: null,
        selectedElementPath: [],
        isSelectionMode: false,
        selectedElementPaths: [],
      };
    }

    case 'MOVE_PAGE': {
      if (!state.currentFlow) return state;

      const pagesEdit = [...state.currentFlow.pages_edit];
      const pagesView = [...state.currentFlow.pages_view];

      // Seite aus pages_edit verschieben
      const [movedPageEdit] = pagesEdit.splice(action.sourceIndex, 1);
      pagesEdit.splice(action.targetIndex, 0, movedPageEdit);

      // Entsprechende Seite in pages_view auch verschieben
      const [movedPageView] = pagesView.splice(action.sourceIndex, 1);
      pagesView.splice(action.targetIndex, 0, movedPageView);

      const newFlow = {
        ...state.currentFlow,
        pages_edit: pagesEdit,
        pages_view: pagesView
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        isDirty: true
      };
    }

    case 'UPDATE_PAGE': {
      if (!state.currentFlow) return state;

      // Bestimme die korrespondierende View-Seiten-ID
      const viewPageId = action.page.id.startsWith('edit-')
        ? action.page.id.replace('edit-', 'view-')
        : action.page.id.replace('view-', 'edit-');

      // Aktualisiere die Seite in pages_edit
      const updatedPagesEdit = state.currentFlow.pages_edit.map(page =>
        page.id === action.page.id ? action.page : page
      );

      // Aktualisiere die View-Seite
      let updatedPagesView;

      if (action.viewPage) {
        // Wenn eine viewPage übergeben wurde, verwende diese
        updatedPagesView = state.currentFlow.pages_view.map(page =>
          page.id === viewPageId ? action.viewPage! : page
        );
      } else {
        // Ansonsten synchronisiere nur Metadaten (layout der View-Page bleibt erhalten)
        updatedPagesView = state.currentFlow.pages_view.map(page => {
          if (page.id === viewPageId || page.id === action.page.id) {
            return {
              ...page,
              title: action.page.title,
              short_title: action.page.short_title,
              icon: action.page.icon,
              // layout wird NICHT übernommen – View-Pages haben ihr eigenes Layout (2_COL_RIGHT_WIDER)
              module_id: action.page.module_id,
              visibility_condition: action.page.visibility_condition
            };
          }
          return page;
        });
      }

      const newFlow = {
        ...state.currentFlow,
        pages_edit: updatedPagesEdit,
        pages_view: updatedPagesView
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlow,
        redoStack: [],
        isDirty: true
      };
    }

    case 'TOGGLE_DIALOG': {
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          [action.payload.dialog]: action.payload.open,
        },
      };
    }

    case 'TOGGLE_SELECTION_MODE': {
      const newIsSelectionMode = !state.isSelectionMode;
      return {
        ...state,
        isSelectionMode: newIsSelectionMode,
        // Beim Deaktivieren des Selektionsmodus die Auswahl leeren
        selectedElementPaths: newIsSelectionMode ? state.selectedElementPaths : [],
      };
    }

    case 'TOGGLE_MULTI_SELECT': {
      if (!state.currentFlow) return state;

      const pathStr = JSON.stringify(action.path);
      const existingIndex = state.selectedElementPaths.findIndex(
        p => JSON.stringify(p) === pathStr
      );

      let newSelectedPaths: number[][];
      if (existingIndex >= 0) {
        // Pfad bereits vorhanden → entfernen (Deselektieren)
        newSelectedPaths = state.selectedElementPaths.filter((_, i) => i !== existingIndex);
      } else {
        // Pfad nicht vorhanden → hinzufügen (Selektieren)
        newSelectedPaths = [...state.selectedElementPaths, action.path];
      }

      // Das zuletzt getogglte Element auch als selectedElement setzen
      const page = state.currentFlow.pages_edit.find(p => p.id === action.pageId);
      const selectedEl = page ? getElementByPath(page.elements, action.path) : null;

      return {
        ...state,
        selectedElementPaths: newSelectedPaths,
        selectedElement: selectedEl,
        selectedElementPath: action.path,
      };
    }

    case 'CLEAR_MULTI_SELECT': {
      return {
        ...state,
        selectedElementPaths: [],
      };
    }

    case 'WRAP_IN_GROUP': {
      if (!state.currentFlow) return state;

      const { paths, groupTitle, groupFieldId, pageId } = action.payload;
      if (paths.length < 1) return state;

      const wrapPage = state.currentFlow.pages_edit.find(p => p.id === pageId);
      if (!wrapPage) return state;

      // Alle Pfade müssen denselben Parent haben (Validierung sollte in App.tsx erfolgen)
      // Sortiere Pfade nach dem letzten Index aufsteigend für die Extraktion
      const sortedPaths = [...paths].sort((a, b) => {
        const lastA = a[a.length - 1];
        const lastB = b[b.length - 1];
        return lastA - lastB;
      });

      // 1. Extrahiere alle Elemente an den gegebenen Pfaden
      const extractedElements: PatternLibraryElement[] = [];
      for (const path of sortedPaths) {
        const element = getElementByPath(wrapPage.elements, path);
        if (element) {
          // Deep copy um Immutabilität zu gewährleisten
          extractedElements.push(JSON.parse(JSON.stringify(element)));
        }
      }

      if (extractedElements.length === 0) return state;

      // 2. Erstelle das neue GroupUIElement
      const newGroupElement: PatternLibraryElement = {
        element: {
          pattern_type: 'GroupUIElement',
          uuid: uuidv4(),
          required: false,
          isCollapsible: false,
          title: { de: groupTitle, en: groupTitle },
          field_id: { field_name: groupFieldId },
          elements: extractedElements
        } as GroupUIElement
      };

      // 3. Entferne die selektierten Elemente (von hinten nach vorne, um Indizes beizubehalten)
      const reverseSortedPaths = [...sortedPaths].reverse();
      let updatedElements = [...wrapPage.elements];

      // Da alle Pfade denselben Parent haben, können wir sie direkt verarbeiten
      const parentPath = sortedPaths[0].slice(0, -1);

      if (parentPath.length === 0) {
        // Top-Level: Direkt aus page.elements entfernen
        const indicesToRemove = reverseSortedPaths.map(p => p[p.length - 1]);
        for (const idx of indicesToRemove) {
          updatedElements.splice(idx, 1);
        }

        // 4. Neues GroupUIElement an der Position des ersten selektierten Elements einfügen
        const insertIndex = sortedPaths[0][sortedPaths[0].length - 1];
        updatedElements.splice(insertIndex, 0, newGroupElement);
      } else {
        // Verschachtelt: Verwende removeElementAtPath und addElementAtPath
        for (const path of reverseSortedPaths) {
          updatedElements = removeElementAtPath(updatedElements, path);
        }

        // Einfügeposition: Der Pfad des ersten Elements
        const insertPath = [...sortedPaths[0]];
        updatedElements = addElementAtPath(updatedElements, insertPath, newGroupElement);
      }

      // 5. Neuen Flow erstellen
      const newFlowWithGroup = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((p: Page) =>
          p.id === pageId
            ? { ...p, elements: updatedElements }
            : p
        ),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: newFlowWithGroup,
        redoStack: [],
        isDirty: true,
        selectedElement: null,
        selectedElementPath: [],
        isSelectionMode: false,
        selectedElementPaths: [],
      };
    }

    case 'UNGROUP': {
      if (!state.currentFlow) return state;

      const { path: ungroupPath, pageId: ungroupPageId } = action.payload;
      if (ungroupPath.length === 0) return state;

      const ungroupPage = state.currentFlow.pages_edit.find(p => p.id === ungroupPageId);
      if (!ungroupPage) return state;

      // 1. Element an dem Pfad finden
      const groupElement = getElementByPath(ungroupPage.elements, ungroupPath);
      if (!groupElement) return state;

      // 2. Prüfen, ob es ein GroupUIElement ist
      if (groupElement.element.pattern_type !== 'GroupUIElement') return state;

      // 3. Kinder extrahieren (deep copy)
      const groupChildren: PatternLibraryElement[] = ((groupElement.element as GroupUIElement).elements || [])
        .map((child: PatternLibraryElement) => JSON.parse(JSON.stringify(child)));

      // 4. Gruppe entfernen und Kinder an derselben Position einfügen
      const groupIndex = ungroupPath[ungroupPath.length - 1];
      const parentPath = ungroupPath.slice(0, -1);
      let ungroupUpdatedElements = [...ungroupPage.elements];

      if (parentPath.length === 0) {
        // Top-Level: Direkt in page.elements arbeiten
        ungroupUpdatedElements.splice(groupIndex, 1, ...groupChildren);
      } else {
        // Verschachtelt: Gruppe entfernen, dann Kinder einfügen
        ungroupUpdatedElements = removeElementAtPath(ungroupUpdatedElements, ungroupPath);

        // Kinder einfügen an der Position der ehemaligen Gruppe (von hinten nach vorne)
        for (let i = groupChildren.length - 1; i >= 0; i--) {
          ungroupUpdatedElements = addElementAtPath(ungroupUpdatedElements, [...parentPath, groupIndex], groupChildren[i]);
        }
      }

      // 5. Neuen Flow erstellen
      const ungroupNewFlow = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((p: Page) =>
          p.id === ungroupPageId
            ? { ...p, elements: ungroupUpdatedElements }
            : p
        ),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: ungroupNewFlow,
        redoStack: [],
        isDirty: true,
        selectedElement: null,
        selectedElementPath: [],
      };
    }

    case 'IMPORT_PAGES': {
      if (!state.currentFlow) return state;

      const { editPages: importedEditPages, viewPages: importedViewPages } = action.payload;
      if (importedEditPages.length === 0) return state;

      const importNewFlow = {
        ...state.currentFlow,
        pages_edit: [...state.currentFlow.pages_edit, ...importedEditPages],
        pages_view: [...state.currentFlow.pages_view, ...importedViewPages],
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: importNewFlow,
        redoStack: [],
        selectedPageId: importedEditPages[0].id, // Erste importierte Seite auswählen
        selectedElement: null,
        selectedElementPath: [],
        isDirty: true,
        isSelectionMode: false,
        selectedElementPaths: [],
      };
    }

    case 'COPY_ELEMENT_TO_PAGE': {
      if (!state.currentFlow) return state;

      const { targetPageId: copyTargetPageId, clonedElement: copyClonedElement, position: copyPosition } = action.payload;

      // Zielseite finden
      const copyTargetPageIndex = state.currentFlow.pages_edit.findIndex(p => p.id === copyTargetPageId);
      if (copyTargetPageIndex === -1) return state;

      const copyTargetPage = state.currentFlow.pages_edit[copyTargetPageIndex];
      let copyUpdatedElements: PatternLibraryElement[];

      if (copyPosition === 'top') {
        copyUpdatedElements = [copyClonedElement, ...copyTargetPage.elements];
      } else {
        copyUpdatedElements = [...copyTargetPage.elements, copyClonedElement];
      }

      const copyNewFlow = {
        ...state.currentFlow,
        pages_edit: state.currentFlow.pages_edit.map((p: Page, idx: number) =>
          idx === copyTargetPageIndex
            ? { ...p, elements: copyUpdatedElements }
            : p
        ),
      };

      return {
        ...state,
        undoStack: [...state.undoStack, state.currentFlow],
        currentFlow: copyNewFlow,
        redoStack: [],
        isDirty: true,
      };
    }

    default:
      return state;
  }
}

export function EditorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  return (
    <EditorContext.Provider value={{ state, dispatch }}>
      {children}
    </EditorContext.Provider>
  );
}

export function useEditor() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
}
